/**
 * ActiveFilterChips - Refero-style removable chip bar for active filters
 * Shows below VehiclesFilterBar; sticky on desktop scroll
 */
import { X, FunnelSimple } from '@phosphor-icons/react';
import { useTranslationV2 } from '@/hooks/useTranslationV2';
import { cn } from '@/lib/utils';

interface ActiveFilters {
  make?: string;
  model?: string;
  priceFrom?: number;
  priceTo?: number;
  yearFrom?: number;
  yearTo?: number;
  mileageTo?: number;
  fuelType?: string;
  transmission?: string;
  location?: string;
  [key: string]: any;
}

interface ActiveFilterChipsProps {
  filters: ActiveFilters;
  selectedType?: string;
  vehicleTypeLabel?: string;
  resultsCount?: number;
  loading?: boolean;
  onRemove: (key: keyof ActiveFilters) => void;
  onClearAll: () => void;
  className?: string;
}

const formatPrice = (n: number) => `${n.toLocaleString('ro-RO')} €`;
const formatKm = (n: number) => `≤ ${n.toLocaleString('ro-RO')} km`;

export const ActiveFilterChips: React.FC<ActiveFilterChipsProps> = ({
  filters,
  selectedType,
  vehicleTypeLabel,
  resultsCount,
  loading,
  onRemove,
  onClearAll,
  className,
}) => {
  const { t } = useTranslationV2('filters');

  const chips: { key: keyof ActiveFilters | '__type'; label: string; removable?: boolean }[] = [];
  if (selectedType && vehicleTypeLabel) chips.push({ key: '__type', label: vehicleTypeLabel, removable: false });
  if (filters.make) chips.push({ key: 'make', label: filters.make });
  if (filters.model) chips.push({ key: 'model', label: filters.model });
  if (filters.yearFrom) chips.push({ key: 'yearFrom', label: `${t('from_year', 'Din')} ${filters.yearFrom}` });
  if (filters.yearTo) chips.push({ key: 'yearTo', label: `${t('to_year', 'Până în')} ${filters.yearTo}` });
  if (filters.priceFrom) chips.push({ key: 'priceFrom', label: `≥ ${formatPrice(filters.priceFrom)}` });
  if (filters.priceTo) chips.push({ key: 'priceTo', label: `≤ ${formatPrice(filters.priceTo)}` });
  if (filters.mileageTo) chips.push({ key: 'mileageTo', label: formatKm(filters.mileageTo) });
  if (filters.mileageFrom) chips.push({ key: 'mileageFrom', label: `≥ ${filters.mileageFrom.toLocaleString('ro-RO')} km` });
  if (filters.fuelType) chips.push({ key: 'fuelType', label: filters.fuelType });
  if (filters.transmission) chips.push({ key: 'transmission', label: filters.transmission });
  if (filters.location) chips.push({ key: 'location', label: filters.location });
  if (filters.powerFrom) chips.push({ key: 'powerFrom', label: `≥ ${filters.powerFrom} CP` });
  if (filters.powerTo) chips.push({ key: 'powerTo', label: `≤ ${filters.powerTo} CP` });
  if (filters.doors) chips.push({ key: 'doors', label: `${filters.doors} ${t('doors', 'Uși').toLowerCase()}` });
  if (filters.euro_norm) chips.push({ key: 'euro_norm', label: filters.euro_norm });
  if (filters.bodyType) chips.push({ key: 'bodyType', label: t(`body_${filters.bodyType}`, filters.bodyType) });
  if (filters.color) chips.push({ key: 'color', label: t(`color_${filters.color}`, filters.color) });
  if (filters.driveType) chips.push({ key: 'driveType', label: t(`drive_${filters.driveType}`, filters.driveType.toUpperCase()) });
  if (filters.conditionTechnical) chips.push({ key: 'conditionTechnical', label: t(`cond_${filters.conditionTechnical}`, filters.conditionTechnical) });
  if (filters.sellerType) chips.push({ key: 'sellerType', label: t(`seller_${filters.sellerType}`, filters.sellerType) });
  if (Array.isArray(filters.features)) {
    filters.features.forEach((f: string) => chips.push({ key: `feat__${f}`, label: t(`feat_${f}`, f) }));
  }

  const hasChips = chips.length > 0;
  if (!hasChips && loading) return null;

  return (
    <div
      className={cn(
        'md:sticky md:top-0 z-30 -mx-4 md:mx-0 px-4 md:px-0 py-2 md:py-3',
        'bg-background/85 backdrop-blur-md border-b border-border/60',
        !hasChips && 'hidden md:block',
        className
      )}
    >
      <div className="flex items-center gap-2 flex-wrap">
        {hasChips ? (
          <>
            <span className="hidden md:inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-primary">
              <FunnelSimple size={14} weight="bold" />
              {t('active_filters', 'Filtre active')}
            </span>
            {chips.map((chip) => (
              <span
                key={String(chip.key)}
                className={cn(
                  'group inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-all duration-200',
                  chip.removable === false
                    ? 'bg-primary text-primary-foreground ring-1 ring-accent/30'
                    : 'bg-primary/10 text-primary ring-1 ring-primary/20 hover:bg-primary hover:text-primary-foreground hover:ring-accent/30'
                )}
              >
                <span className="leading-none">{chip.label}</span>
                {chip.removable !== false && (
                  <button
                    type="button"
                    onClick={() => onRemove(chip.key as keyof ActiveFilters)}
                    aria-label={`Remove ${chip.label}`}
                    className="flex h-4 w-4 items-center justify-center rounded-full bg-foreground/0 group-hover:bg-primary-foreground/20 transition-colors"
                  >
                    <X size={10} weight="bold" />
                  </button>
                )}
              </span>
            ))}
            <button
              type="button"
              onClick={onClearAll}
              className="ml-auto text-xs font-semibold text-accent hover:text-accent/80 underline-offset-2 hover:underline"
            >
              {t('clear_all', 'Șterge tot')}
            </button>
          </>
        ) : (
          <span className="text-xs text-muted-foreground">
            {t('no_active_filters', 'Niciun filtru activ — explorează toate vehiculele')}
          </span>
        )}
        {typeof resultsCount === 'number' && !loading && (
          <span className="hidden md:inline-flex ml-auto items-center gap-1 text-xs font-bold text-foreground tabular-nums">
            <span className="text-accent">{resultsCount.toLocaleString('ro-RO')}</span>
            <span className="text-muted-foreground font-normal">{t('results_found', 'rezultate')}</span>
          </span>
        )}
      </div>
    </div>
  );
};
